// src/Dashboard/admin/sections/CoursesSection.jsx
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, BookOpen, Plus, Pencil, Trash2, Search, Building2 } from "lucide-react";
import { ModalShell, SelectField, MiniStatusCard, Badge } from "../components/ui";

function safeNumber(x, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function pct(n) {
  return Math.max(0, Math.min(100, Math.round(safeNumber(n))));
}

function safeText(v) {
  return String(v ?? "").trim();
}

function slugify(input) {
  return safeText(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}

/**
 * Uses reportsSummary.courses (already scope-aware from /reports/summary).
 * Each course row typically looks like:
 * {
 *   course_slug,
 *   enrolled,
 *   learners_completed,
 *   learners_graduated,
 *   total_attempts,
 *   pass_rate_pct
 * }
 *
 * UI-only “Add Course” adds local rows (not persisted).
 */
export default function CoursesSection({
  // from AdminDashboard
  level,
  targetId,
  scopeLabel,
  hierarchy, // not required yet, but kept for later
  reportsSummary,
  reportsLoading = false,
  reportsError = "",
  onReloadReports,
  refreshNonce,
  scopeChips = [],
}) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  // UI-only local additions
  const [localCourses, setLocalCourses] = useState([]);

  // UI-only Add modal
  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addSlug, setAddSlug] = useState("");
  const [addLessons, setAddLessons] = useState("");
  const [addStatus, setAddStatus] = useState("active");
  const [addDesc, setAddDesc] = useState("");
  const [addNote, setAddNote] = useState("");

  // keep slug in sync with title until user edits slug manually
  const [slugTouched, setSlugTouched] = useState(false);
  useEffect(() => {
    if (slugTouched) return;
    const s = slugify(addTitle);
    setAddSlug(s);
  }, [addTitle, slugTouched]);

  const apiCourses = useMemo(() => {
    const arr = reportsSummary?.courses || reportsSummary?.courseStats || [];
    return Array.isArray(arr) ? arr : [];
  }, [reportsSummary]);

  const mergedCourses = useMemo(() => {
    // show local additions first, then API
    const seen = new Set();
    const out = [];

    for (const c of localCourses) {
      const slug = safeText(c.course_slug);
      if (!slug) continue;
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push(c);
    }
    for (const c of apiCourses) {
      const slug = safeText(c.course_slug || c.slug);
      if (!slug) continue;
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push({
        ...c,
        course_slug: slug,
      });
    }
    return out;
  }, [localCourses, apiCourses]);

  const filtered = useMemo(() => {
    const term = safeText(q).toLowerCase();
    if (!term) return mergedCourses;
    return mergedCourses.filter((c) => {
      const slug = safeText(c.course_slug || c.slug).toLowerCase();
      const title = safeText(c.course_title || c.title || c.name).toLowerCase();
      return slug.includes(term) || title.includes(term);
    });
  }, [mergedCourses, q]);

  const totals = useMemo(() => {
    const arr = filtered || [];
    const sum = (key) => arr.reduce((acc, x) => acc + safeNumber(x?.[key]), 0);

    const coursesCount = arr.length;
    const enrolled = sum("enrolled");
    const completed = sum("learners_completed");
    const graduated = sum("learners_graduated");
    const attempts = sum("total_attempts");

    // weighted-ish average pass rate by enrolled (fallback to simple avg)
    const withRate = arr.filter((x) => x?.pass_rate_pct !== undefined && x?.pass_rate_pct !== null);
    const weighted = withRate.reduce(
      (acc, x) => {
        const w = Math.max(1, safeNumber(x?.enrolled, 0));
        acc.wSum += w;
        acc.rSum += w * safeNumber(x?.pass_rate_pct, 0);
        return acc;
      },
      { wSum: 0, rSum: 0 }
    );
    const passRate = weighted.wSum > 0 ? Math.round(weighted.rSum / weighted.wSum) : 0;

    return { coursesCount, enrolled, completed, graduated, attempts, passRate };
  }, [filtered]);

  const effectiveScopeText = useMemo(() => {
    if ((scopeChips || []).length) return scopeChips.join(" • ");
    return scopeLabel || (level === "union" ? "Union (All)" : `${level} ${targetId || ""}`.trim());
  }, [scopeChips, scopeLabel, level, targetId]);

  async function handleRefresh() {
    try {
      await onReloadReports?.();
    } catch (e) {
      // keep silent; parent already shows errors
      // eslint-disable-next-line no-console
      console.warn("[courses.refresh] failed:", e);
    }
  }

  function resetAddForm() {
    setAddTitle("");
    setAddSlug("");
    setAddLessons("");
    setAddStatus("active");
    setAddDesc("");
    setAddNote("");
    setSlugTouched(false);
  }

  function openAdd() {
    resetAddForm();
    setAddOpen(true);
  }

  function saveAdd() {
    setAddNote("");

    const title = safeText(addTitle);
    const slug = safeText(addSlug);
    if (!title) return setAddNote("Title is required.");
    if (!slug) return setAddNote("Slug is required.");

    const row = {
      course_slug: slug,
      course_title: title,
      status: addStatus,
      total_lessons: safeNumber(addLessons, 0),
      description: addDesc,
      // analytics placeholders (until backend supports)
      enrolled: 0,
      learners_completed: 0,
      learners_graduated: 0,
      pass_rate_pct: 0,
      total_attempts: 0,
      __local: true,
    };

    setLocalCourses((prev) => [row, ...(prev || [])]);
    setAddOpen(false);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Courses</h2>
            <p className="text-xs text-slate-500">
              Analytics pulled from Reports Summary (scope enforced). “Add Course” is UI-only for now.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={reportsLoading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold shadow-sm"
              title="UI-only"
            >
              <Plus size={16} />
              Add Course
            </button>
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
          <Building2 size={14} />
          <span>
            Scope: <span className="font-semibold">{effectiveScopeText}</span>
          </span>
        </div>

        {reportsError ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
            {reportsError}
          </div>
        ) : null}

        {/* Summary cards */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <MiniStatusCard title="Courses" value={totals.coursesCount} subtitle="in this scope" icon={BookOpen} />
          <MiniStatusCard title="Enrolled" value={totals.enrolled} subtitle="total learners" icon={BookOpen} />
          <MiniStatusCard title="Completed" value={totals.completed} subtitle="total" icon={BookOpen} />
          <MiniStatusCard title="Graduated" value={totals.graduated} subtitle="certificates" icon={BookOpen} />
          <MiniStatusCard title="Pass Rate" value={`${pct(totals.passRate)}%`} subtitle="weighted by enrolled" icon={BookOpen} />
        </div>

        {/* Search */}
        <div className="mt-5 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by slug or title…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
          </div>
          <button
            onClick={() => setQ("")}
            className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">Course Analytics</h3>
          <div className="text-xs text-slate-500">
            {reportsLoading ? "Loading…" : `${filtered.length} rows`}
          </div>
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
              <tr>
                <th className="p-3 text-left">Course</th>
                <th className="p-3 text-center">Enrolled</th>
                <th className="p-3 text-center">Completed</th>
                <th className="p-3 text-center">Graduated</th>
                <th className="p-3 text-center">Attempts</th>
                <th className="p-3 text-center">Pass Rate</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((c) => {
                const slug = safeText(c.course_slug || c.slug);
                const title = safeText(c.course_title || c.title || c.name) || slug;

                const isLocal = !!c.__local;

                return (
                  <tr
                    key={slug}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{title}</div>
                          <div className="text-xs text-slate-500 font-mono truncate">{slug}</div>
                        </div>
                        {isLocal ? (
                          <Badge tone="neutral" label="UI-only" />
                        ) : null}
                      </div>
                    </td>

                    <td className="p-3 text-center">{safeNumber(c.enrolled)}</td>
                    <td className="p-3 text-center">{safeNumber(c.learners_completed)}</td>
                    <td className="p-3 text-center">{safeNumber(c.learners_graduated)}</td>
                    <td className="p-3 text-center">{safeNumber(c.total_attempts)}</td>
                    <td className="p-3 text-center">{pct(c.pass_rate_pct)}%</td>

                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelected(c)}
                          className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          View
                        </button>

                        <button
                          onClick={() => alert("Edit UI later (we’ll wire backend after).")}
                          className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                          title="UI later"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          onClick={() => alert("Delete UI later (we’ll wire backend after).")}
                          className="p-2 rounded-xl text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="UI later"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!reportsLoading && filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">
                    No courses found for this scope.
                  </td>
                </tr>
              ) : null}

              {reportsLoading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          Note: This uses <span className="font-mono">reportsSummary.courses</span>. Later we can add a dedicated{" "}
          <span className="font-mono">/api/admin/courses/analytics</span> endpoint if you want richer metrics.
        </div>
      </div>

      {/* Details Drawer */}
      {selected ? <CourseDrawer course={selected} onClose={() => setSelected(null)} /> : null}

      {/* Add Course Modal (UI-only) */}
      {addOpen ? (
        <ModalShell
          title="Add course (UI-only)"
          subtitle="This saves locally in the UI for now. Backend wiring comes later."
          onClose={() => setAddOpen(false)}
          footer={
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-slate-500 truncate">
                {addNote ? <span className="text-amber-700 dark:text-amber-200">{addNote}</span> : " "}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveAdd}
                  className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold"
                >
                  Save
                </button>
              </div>
            </div>
          }
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Title</label>
              <input
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                placeholder="e.g. Discover Bible Lessons"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Slug</label>
              <input
                value={addSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setAddSlug(e.target.value);
                }}
                placeholder="e.g. discover"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm font-mono"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                Used in URLs and reports (keep lowercase + hyphens).
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Total lessons</label>
              <input
                value={addLessons}
                onChange={(e) => setAddLessons(e.target.value)}
                placeholder="e.g. 26"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
              />
            </div>

            <SelectField
              label="Status"
              value={addStatus}
              onChange={setAddStatus}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Description (optional)
              </label>
              <textarea
                value={addDesc}
                onChange={(e) => setAddDesc(e.target.value)}
                rows={4}
                placeholder="Short description…"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
              />
            </div>

            <div className="md:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 text-xs text-slate-600 dark:text-slate-300">
              When we wire backend later, this will call a real endpoint and refresh analytics automatically.
            </div>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}

function CourseDrawer({ course, onClose }) {
  const slug = safeText(course?.course_slug || course?.slug) || "course";
  const title = safeText(course?.course_title || course?.title || course?.name) || slug;

  const enrolled = safeNumber(course?.enrolled);
  const completed = safeNumber(course?.learners_completed);
  const graduated = safeNumber(course?.learners_graduated);
  const attempts = safeNumber(course?.total_attempts);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Course details</div>
            <div className="text-lg font-semibold truncate">{title}</div>
            <div className="text-xs text-slate-500 font-mono truncate">{slug}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <XIcon />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto">
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Enrolled" value={enrolled} />
            <Metric label="Completed" value={completed} />
            <Metric label="Graduated" value={graduated} />
            <Metric label="Attempts" value={attempts} />
            <Metric label="Pass rate" value={`${pct(course?.pass_rate_pct)}%`} />
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950">
            <div className="text-sm font-semibold mb-1">Next wiring ideas</div>
            <div className="text-xs text-slate-500">
              Per-lesson averages, completion distribution, certificates list, and “Manage lessons/tests” actions.
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
